import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

/**
 * Fase 11.5 — Solicitar reset de contraseña (magic link).
 *
 * Comportamiento:
 * - User ingresa email + acepta.
 * - Backend SIEMPRE retorna 200 (evita enumeración de cuentas).
 * - Si la cuenta existe, llega un email con link `/reset-password?token=...`
 * - Rate limit 3/h/IP + 5/h/email.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !email.includes("@")) {
      setError("Ingresa un correo válido");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/request", { email: email.trim() });
      setSent(true);
      toast.success("Revisa tu bandeja");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        setError(
          formatApiError(e?.response?.data?.detail) ||
            "Has solicitado demasiados restablecimientos. Intenta más tarde.",
        );
      } else {
        // El backend NUNCA debería retornar 4xx/5xx con email inexistente;
        // pero si pasa cualquier error de red, mostramos genérico.
        setError(
          formatApiError(e?.response?.data?.detail) ||
            "No se pudo procesar la solicitud. Intenta de nuevo.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="container-prose py-20">
        <div className="max-w-md mx-auto border border-emerald-200 bg-emerald-50/40 p-8 rounded-sm" data-testid="forgot-password-sent">
          <p className="text-xs uppercase tracking-[0.22em] text-emerald-800 font-medium mb-3">
            Solicitud enviada
          </p>
          <h1 className="font-serif text-2xl text-gray-900 mb-3">Revisa tu correo</h1>
          <p className="text-sm text-gray-800 mb-4">
            Si la cuenta <strong>{email.trim()}</strong> existe, te enviamos un enlace para
            restablecer tu contraseña. El enlace expira en <strong>1 hora</strong> y solo
            puede usarse una vez.
          </p>
          <p className="text-sm text-gray-700 mb-4">
            <strong>¿No recibes el correo?</strong> Revisa tu carpeta de spam o promociones.
            Si no llega en 5 minutos, escríbenos a{" "}
            <a className="text-primary underline" href="mailto:contacto@procure.justsapiens.com">
              contacto@procure.justsapiens.com
            </a>.
          </p>
          <div className="flex gap-3 pt-3 border-t border-emerald-200">
            <Link to="/login" className="text-sm text-primary hover:underline">
              ← Volver a iniciar sesión
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-prose py-20">
      <div className="max-w-md mx-auto">
        <div className="flex justify-center mb-6">
          <Link to="/" aria-label="JustSapiens Procure — Inicio">
            <img
              src="/brand/logo-stacked-positive.png"
              alt="JustSapiens Procure"
              className="h-32 w-auto object-contain"
              loading="eager"
              decoding="async"
            />
          </Link>
        </div>
        <div className="border border-gray-200 bg-white p-8">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-3">
            Recuperar acceso
          </p>
          <h1 className="font-serif text-3xl text-gray-900 mb-2">¿Olvidaste tu contraseña?</h1>
          <p className="text-sm text-gray-600">
            Ingresa tu correo electrónico y te enviaremos un enlace para crear una nueva
            contraseña. El enlace expira en 1 hora.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-password-form">
          <div className="space-y-1.5">
            <Label htmlFor="email">Correo electrónico</Label>
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-password-email"
              className="rounded-sm h-11"
              placeholder="tu.correo@entidad.gov.co"
            />
          </div>

          {error && (
            <div
              data-testid="forgot-password-error"
              className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full rounded-sm h-11"
            data-testid="forgot-password-submit"
          >
            {submitting ? "Enviando…" : "Enviar enlace de recuperación"}
          </Button>
        </form>

        <p className="text-sm text-gray-600 mt-6 text-center">
          ¿Te acordaste?{" "}
          <Link to="/login" data-testid="forgot-back-to-login" className="text-primary hover:underline">
            Volver a iniciar sesión
          </Link>
        </p>
        </div>
      </div>
    </div>
  );
}
